"use client";

import Link from "next/link";
import Image from "next/image";
import { useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  Award,
  ChevronDown,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Medal,
  Newspaper,
  User,
  Vote,
  Activity,
  Calendar,
  Target,
  Info,
  Shirt,
  BarChart3,
  Trophy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ThemeToggle } from "@/components/shared/theme-toggle";
import { AktualnyTicker } from "./aktualny-ticker";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import type { Profil } from "@/types/database";
import { createClient } from "@/lib/supabase/client";
import ClankyMegaMenu from "@/components/zoh/clanky/ClankyMegaMenu";
import MegaMenu from "@/components/shared/MegaMenu";

interface HeaderProps {
  user: SupabaseUser | null;
  profil: Profil | null;
}

type SekciaMegaMenu = "turnaj" | "reprezentácia" | "hlasovanie" | "info" | null;

const TRIEDY_NAV_POLOZKA = cn(
  "px-3 transition-colors relative h-14 rounded-none border-b-2 border-transparent",
  "hover:bg-accent hover:text-accent-foreground flex items-center gap-1 sm:gap-2",
);

const TRIEDY_MOBILNY_ODKAZ = cn(
  "text-base font-medium px-4 py-3 rounded-lg transition-colors",
  "flex items-center gap-3 sm:gap-4",
);

const TRIEDY_ODHLASIT = cn(
  "text-base px-4 py-3 rounded-lg transition-colors",
  "text-destructive hover:text-destructive hover:bg-accent",
  "block w-full text-left justify-start h-auto font-medium",
);

export function Header({ user, profil }: HeaderProps) {
  const nazovWebu = "ZOH 2026";
  const textOdhlasit = "Odhlásiť";
  const textPrihlasit = "Prihlásiť";
  const textProfil = "Profil";
  const textAdministracia = "Administrácia";
  const textClanky = "Články";
  const textPouzivatel = "Používateľ";

  const cesta = usePathname();
  const router = useRouter();

  const [jeMobilneMenuOtvorene, nastavJeMobilneMenuOtvorene] = useState(false);
  const [otvoreneSekcie, setOtvoreneSekcie] = useState<Set<string>>(new Set());

  const [otvorenaSekciaDesktop, nastavOtvorenuSekciuDesktop] =
    useState<SekciaMegaMenu>(null);
  const [jeMenuClankyOtvorene, nastavJeMenuClankyOtvorene] = useState(false);
  const [timeoutZavretia, nastavTimeoutZavretia] =
    useState<NodeJS.Timeout | null>(null);

  const jeAdmin = profil?.rola === "admin";

  const jeAktivnaSekciaClanky = useMemo(
    () => cesta.startsWith("/clanky"),
    [cesta],
  );

  async function odhlasitPouzivatela() {
    const supabase = createClient();
    await supabase.auth.signOut();
    router.push("/");
    router.refresh();
  }

  function zavriDesktopMenu() {
    const timeout = setTimeout(() => {
      nastavOtvorenuSekciuDesktop(null);
      nastavJeMenuClankyOtvorene(false);
    }, 150); // 150ms delay before closing
    nastavTimeoutZavretia(timeout);
  }

  function zavriMobilneMenu() {
    nastavJeMobilneMenuOtvorene(false);
    setOtvoreneSekcie(new Set());
  }

  function onZmenaStavuMobilnehoMenu(otvorene: boolean) {
    if (!otvorene) setOtvoreneSekcie(new Set());
    nastavJeMobilneMenuOtvorene(otvorene);
  }

  function otvorMobilnuSekciu(sekcia: string) {
    setOtvoreneSekcie((prev) => {
      const nove = new Set(prev);
      if (nove.has(sekcia)) {
        nove.delete(sekcia);
      } else {
        nove.add(sekcia);
      }
      return nove;
    });
  }

  function zrusTimeoutZavretia() {
    if (timeoutZavretia) {
      clearTimeout(timeoutZavretia);
      nastavTimeoutZavretia(null);
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background border-border shadow-sm">
      {/* Hlavná navigácia */}
      <div className="container mx-auto flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center space-x-2 shrink-0">
            <Image
              src="/logo.png"
              alt="ZOH 2026 Logo"
              width={32}
              height={32}
              className="h-8 w-auto rounded-full border-2 border-primary"
              priority
            />
            <span className="font-bold text-lg tracking-tight">
              {nazovWebu}
            </span>
          </Link>

          <nav
            className="hidden md:flex items-center gap-1 relative"
            onMouseLeave={zavriDesktopMenu}
          >
            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  TRIEDY_NAV_POLOZKA,
                  otvorenaSekciaDesktop === "turnaj"
                    ? "border-primary text-primary font-semibold"
                    : "text-muted-foreground",
                )}
                aria-haspopup="true"
                aria-expanded={otvorenaSekciaDesktop === "turnaj"}
                onPointerEnter={() => nastavOtvorenuSekciuDesktop("turnaj")}
                onFocus={() => nastavOtvorenuSekciuDesktop("turnaj")}
              >
                <Medal className="h-4 w-4" />
                Turnaj
              </Button>

              <MegaMenu
                isOpen={otvorenaSekciaDesktop === "turnaj"}
                onClose={() => nastavOtvorenuSekciuDesktop(null)}
                section="turnaj"
                onMouseEnter={() => {
                  zrusTimeoutZavretia();
                  nastavOtvorenuSekciuDesktop("turnaj");
                }}
                onMouseLeave={() => nastavOtvorenuSekciuDesktop(null)}
              />
            </div>

            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  TRIEDY_NAV_POLOZKA,
                  otvorenaSekciaDesktop === "reprezentácia"
                    ? "border-primary text-primary font-semibold"
                    : "text-muted-foreground",
                )}
                aria-haspopup="true"
                aria-expanded={otvorenaSekciaDesktop === "reprezentácia"}
                onPointerEnter={() =>
                  nastavOtvorenuSekciuDesktop("reprezentácia")
                }
                onFocus={() => nastavOtvorenuSekciuDesktop("reprezentácia")}
              >
                <Award className="h-4 w-4" />
                Reprezentácia
              </Button>

              <MegaMenu
                isOpen={otvorenaSekciaDesktop === "reprezentácia"}
                onClose={() => nastavOtvorenuSekciuDesktop(null)}
                section="reprezentácia"
                onMouseEnter={() => {
                  zrusTimeoutZavretia();
                  nastavOtvorenuSekciuDesktop("reprezentácia");
                }}
                onMouseLeave={() => nastavOtvorenuSekciuDesktop(null)}
              />
            </div>

            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  TRIEDY_NAV_POLOZKA,
                  otvorenaSekciaDesktop === "hlasovanie"
                    ? "border-primary text-primary font-semibold"
                    : "text-muted-foreground",
                )}
                aria-haspopup="true"
                aria-expanded={otvorenaSekciaDesktop === "hlasovanie"}
                onPointerEnter={() => nastavOtvorenuSekciuDesktop("hlasovanie")}
                onFocus={() => nastavOtvorenuSekciuDesktop("hlasovanie")}
              >
                <Vote className="h-4 w-4" />
                Hlasovanie
              </Button>

              <MegaMenu
                isOpen={otvorenaSekciaDesktop === "hlasovanie"}
                onClose={() => nastavOtvorenuSekciuDesktop(null)}
                section="hlasovanie"
                onMouseEnter={() => {
                  zrusTimeoutZavretia();
                  nastavOtvorenuSekciuDesktop("hlasovanie");
                }}
                onMouseLeave={() => nastavOtvorenuSekciuDesktop(null)}
              />
            </div>

            <div className="relative">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  TRIEDY_NAV_POLOZKA,
                  otvorenaSekciaDesktop === "info"
                    ? "border-primary text-primary font-semibold"
                    : "text-muted-foreground",
                )}
                aria-haspopup="true"
                aria-expanded={otvorenaSekciaDesktop === "info"}
                onPointerEnter={() => nastavOtvorenuSekciuDesktop("info")}
                onFocus={() => nastavOtvorenuSekciuDesktop("info")}
              >
                <FileText className="h-4 w-4" />
                Info
              </Button>

              <MegaMenu
                isOpen={otvorenaSekciaDesktop === "info"}
                onClose={() => nastavOtvorenuSekciuDesktop(null)}
                section="info"
                onMouseEnter={() => {
                  zrusTimeoutZavretia();
                  nastavOtvorenuSekciuDesktop("info");
                }}
                onMouseLeave={() => nastavOtvorenuSekciuDesktop(null)}
              />
            </div>

            <div className="relative">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className={cn(
                    TRIEDY_NAV_POLOZKA,
                    jeAktivnaSekciaClanky && !jeMenuClankyOtvorene
                      ? "border-primary text-primary font-semibold"
                      : "text-muted-foreground",
                  )}
                  onPointerEnter={() => nastavOtvorenuSekciuDesktop(null)}
                  onFocus={() => nastavOtvorenuSekciuDesktop(null)}
                >
                  <Link href="/clanky">
                    <Newspaper className="h-4 w-4" />
                    {textClanky}
                  </Link>
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "px-1 transition-colors relative h-14 rounded-none border-b-2 border-transparent hover:bg-accent hover:text-accent-foreground",
                    jeMenuClankyOtvorene
                      ? "border-primary text-primary font-semibold"
                      : "text-muted-foreground",
                  )}
                  aria-haspopup="true"
                  aria-expanded={jeMenuClankyOtvorene}
                  onClick={() => {
                    nastavOtvorenuSekciuDesktop(null);
                    nastavJeMenuClankyOtvorene((v) => !v);
                  }}
                  onPointerEnter={() => nastavOtvorenuSekciuDesktop(null)}
                  onFocus={() => nastavOtvorenuSekciuDesktop(null)}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>

              <ClankyMegaMenu
                isOpen={jeMenuClankyOtvorene}
                onClose={() => nastavJeMenuClankyOtvorene(false)}
              />
            </div>

            {/* NHL Live - predposledná položka */}
            <Button
              asChild
              variant="ghost"
              size="sm"
              className={cn(
                TRIEDY_NAV_POLOZKA,
                cesta === "/live-nhl"
                  ? "border-primary text-primary font-semibold"
                  : "text-muted-foreground",
              )}
            >
              <Link href="/live-nhl" className="flex items-center gap-1">
                <Activity className="h-4 w-4" />
                NHL Live
              </Link>
            </Button>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle />

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="relative h-9 w-9 rounded-full"
                  aria-label="Používateľské menu"
                >
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {profil?.zobrazovane_meno || textPouzivatel}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuItem asChild>
                  <Link href="/profil" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    <span>{textProfil}</span>
                  </Link>
                </DropdownMenuItem>

                {jeAdmin && (
                  <DropdownMenuItem asChild>
                    <Link href="/admin" className="cursor-pointer">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>{textAdministracia}</span>
                    </Link>
                  </DropdownMenuItem>
                )}

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={odhlasitPouzivatela}
                  className="text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{textOdhlasit}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" asChild aria-label="Prihlásiť sa">
              <Link href="/prihlasenie">{textPrihlasit}</Link>
            </Button>
          )}

          <Sheet
            open={jeMobilneMenuOtvorene}
            onOpenChange={onZmenaStavuMobilnehoMenu}
          >
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Otvoriť menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>

            <SheetContent side="right" className="w-4/5 sm:w-72 p-0">
              <SheetHeader className="p-6 pb-0">
                <SheetTitle className="text-left">{nazovWebu}</SheetTitle>
              </SheetHeader>

              <div className="flex-1 p-6 pt-2">
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <ThemeToggle />
                </div>

                <div className="flex flex-col gap-2 sm:gap-3">
                  {/* Turnaj */}
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors">
                      <div className="flex items-center gap-3">
                        <Medal className="h-4 w-4 text-primary" />
                        <span className="text-base font-medium">Turnaj</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="p-2 transition-colors hover:bg-accent"
                        onClick={() => otvorMobilnuSekciu("turnaj")}
                      >
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            otvoreneSekcie.has("turnaj") ? "rotate-180" : "",
                          )}
                        />
                      </Button>
                    </div>
                    {otvoreneSekcie.has("turnaj") && (
                      <div className="px-4 py-2 space-y-1">
                        <Link
                          href="/skupiny"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Target className="h-3 w-3" />
                          Skupiny
                        </Link>
                        <Link
                          href="/tabulka"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <BarChart3 className="h-3 w-3" />
                          Hlavná tabuľka
                        </Link>
                        <Link
                          href="/pavuk"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Medal className="h-3 w-3" />
                          Pavúk
                        </Link>
                        <Link
                          href="/zapasy"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Activity className="h-3 w-3" />
                          Zápasy
                        </Link>
                        <Link
                          href="/strelci"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Trophy className="h-3 w-3" />
                          Strelci
                        </Link>
                        <Link
                          href="/kalendar-zapasov"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Calendar className="h-3 w-3" />
                          Kalendár
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Reprezentácia */}
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors">
                      <div className="flex items-center gap-3">
                        <Award className="h-4 w-4 text-primary" />
                        <span className="text-base font-medium">
                          Reprezentácia
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="p-2 transition-colors hover:bg-accent"
                        onClick={() => otvorMobilnuSekciu("reprezentacia")}
                      >
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            otvoreneSekcie.has("reprezentacia")
                              ? "rotate-180"
                              : "",
                          )}
                        />
                      </Button>
                    </div>
                    {otvoreneSekcie.has("reprezentacia") && (
                      <div className="px-4 py-2 space-y-1">
                        <Link
                          href="/slovensko"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Award className="h-3 w-3" />
                          Reprezentácia
                        </Link>
                        <Link
                          href="/navrh-hraca"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Target className="h-3 w-3" />
                          Navrhnúť hráča
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Hlasovanie */}
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors">
                      <div className="flex items-center gap-3">
                        <Vote className="h-4 w-4 text-primary" />
                        <span className="text-base font-medium">
                          Hlasovanie
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="p-2 transition-colors hover:bg-accent"
                        onClick={() => otvorMobilnuSekciu("hlasovanie")}
                      >
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            otvoreneSekcie.has("hlasovanie")
                              ? "rotate-180"
                              : "",
                          )}
                        />
                      </Button>
                    </div>
                    {otvoreneSekcie.has("hlasovanie") && (
                      <div className="px-4 py-2 space-y-1">
                        <Link
                          href="/hlasovanie"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Vote className="h-3 w-3" />
                          Hlasovanie
                        </Link>
                        <Link
                          href="/vysledky"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <BarChart3 className="h-3 w-3" />
                          Výsledky hlasovania
                        </Link>
                        <Link
                          href="/galeria"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Activity className="h-3 w-3" />
                          Galéria nominácií
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors">
                      <div className="flex items-center gap-3">
                        <Info className="h-4 w-4 text-primary" />
                        <span className="text-base font-medium">Info</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="p-2 transition-colors hover:bg-accent"
                        onClick={() => otvorMobilnuSekciu("info")}
                      >
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform",
                            otvoreneSekcie.has("info") ? "rotate-180" : "",
                          )}
                        />
                      </Button>
                    </div>
                    {otvoreneSekcie.has("info") && (
                      <div className="px-4 py-2 space-y-1">
                        <Link
                          href="/dresy"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <Shirt className="h-3 w-3" />
                          Dresy
                        </Link>
                        <Link
                          href="/statistiky-ihf"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <BarChart3 className="h-3 w-3" />
                          Štatistiky IHF
                        </Link>
                        <Link
                          href="/pravidla"
                          onClick={zavriMobilneMenu}
                          className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground"
                        >
                          <FileText className="h-3 w-3" />
                          Pravidlá
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Články - len redirect */}
                  <Link
                    href="/clanky"
                    onClick={zavriMobilneMenu}
                    className={cn(
                      TRIEDY_MOBILNY_ODKAZ,
                      "block",
                      cesta.startsWith("/clanky")
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Newspaper className="h-4 w-4" />
                      <span className="text-base font-medium">
                        {textClanky}
                      </span>
                    </div>
                  </Link>

                  {/* TODO: Budúce rozšírenie - dynamické načítanie článkov
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent transition-colors">
                      <span className="text-base font-medium">{textClanky}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="p-2 transition-colors hover:bg-accent"
                        onClick={() => otvorMobilnuSekciu("clanky")}
                      >
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform",
                          otvoreneSekcie.has("clanky") ? "rotate-180" : ""
                        )} />
                      </Button>
                    </div>
                    {otvoreneSekcie.has("clanky") && (
                      <div className="px-4 py-2 space-y-1">
                        <Link href="/skupiny" onClick={zavriMobilneMenu} className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground">
                          <Target className="h-3 w-3" />
                          Skupiny
                        </Link>
                        <Link href="/zapasy" onClick={zavriMobilneMenu} className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground">
                          <Activity className="h-3 w-3" />
                          Zápasy
                        </Link>
                        <Link href="/kalendar-zapasov" onClick={zavriMobilneMenu} className="flex items-center gap-2 p-2 text-sm text-muted-foreground hover:text-foreground">
                          <Calendar className="h-3 w-3" />
                          Kalendár
                        </Link>
                      </div>
                    )}
                  </div>
                  */}

                  {/* NHL Live - posledná položka */}
                  <Link
                    href="/live-nhl"
                    onClick={zavriMobilneMenu}
                    className={cn(
                      TRIEDY_MOBILNY_ODKAZ,
                      "block",
                      cesta === "/live-nhl"
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent",
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Activity className="h-4 w-4" />
                      <span className="text-base font-medium">NHL Live</span>
                    </div>
                  </Link>

                  <div className="border-t pt-2 mt-2 sm:pt-4 sm:mt-4">
                    {user ? (
                      <>
                        <Link
                          href="/profil"
                          onClick={zavriMobilneMenu}
                          className={cn(
                            TRIEDY_MOBILNY_ODKAZ,
                            "block",
                            cesta === "/profil"
                              ? "bg-accent text-accent-foreground"
                              : "hover:bg-accent",
                          )}
                        >
                          {textProfil}
                        </Link>
                        {jeAdmin && (
                          <Link
                            href="/admin"
                            onClick={zavriMobilneMenu}
                            className={cn(
                              TRIEDY_MOBILNY_ODKAZ,
                              "block",
                              cesta.startsWith("/admin")
                                ? "bg-accent text-accent-foreground"
                                : "hover:bg-accent",
                            )}
                          >
                            {textAdministracia}
                          </Link>
                        )}
                        <Button
                          variant="ghost"
                          onClick={async () => {
                            await odhlasitPouzivatela();
                            zavriMobilneMenu();
                          }}
                          className={TRIEDY_ODHLASIT}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          {textOdhlasit}
                        </Button>
                      </>
                    ) : (
                      <Button
                        asChild
                        onClick={zavriMobilneMenu}
                        className="w-full bg-primary text-primary-foreground py-6 rounded-lg hover:bg-primary/90 transition-colors text-center font-bold"
                      >
                        <Link href="/prihlasenie">{textPrihlasit}</Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Aktualny ticker - druhý riadok */}
      <AktualnyTicker />
    </header>
  );
}
